import React, { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SparePartAuditDetailDialog, {
  buildSparePartAuditSummary,
  sparePartAuditActionLabel,
  sparePartAuditEntityLabel,
  sparePartAuditTicketNumber,
} from "../components/SparePartAuditDetailDialog";
import WarehouseTabs from "../components/WarehouseTabs";
import { Button } from "../components/ui/button";
import {
  DataValue,
  PassiveBadge,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableRefreshingRow,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";
import {
  DateRangeSelector,
  TopLevelFilterArea,
  TopLevelFilterClear,
  TopLevelFilterField,
  TopLevelFilterRow,
  TopLevelFilterSearch,
  TopLevelSelectFilter,
  TopLevelTextFilter,
  hasTopLevelFilterConstraints,
  useSearchFilterState,
} from "../components/ui/search-filter";

const FILTER_KEYS = ["action", "entity_type", "ticket_number", "actor", "summary"];
const EMPTY_FILTERS = Object.freeze(Object.fromEntries(FILTER_KEYS.map((key) => [key, ""])));
const ACTION_OPTIONS = [
  { value: "adjust_stock", label: "Adjust Stock" },
  { value: "create_transfer", label: "Create Transfer" },
  { value: "submit_transfer_request", label: "Submit Transfer Request" },
  { value: "reject_transfer_request", label: "Reject Transfer Request" },
  { value: "cancel_transfer_request", label: "Cancel Transfer Request" },
  { value: "approve_transfer_request", label: "Approve Transfer Request" },
  { value: "edit_approved_transfer_request", label: "Edit Approved Transfer Request" },
  { value: "receive_transfer_request", label: "Receive Transfer Request" },
  { value: "create_hold", label: "Create Hold" },
  { value: "use_hold", label: "Use Hold" },
  { value: "cancel_hold", label: "Cancel Hold" },
];
const ENTITY_OPTIONS = [
  { value: "item", label: "Item" },
  { value: "transfer", label: "Transfer" },
  { value: "transfer_request", label: "Transfer Request" },
  { value: "hold", label: "Hold" },
];
const BADGE_TONES = {
  create_item: "success",
  update_item: "info",
  delete_item: "danger",
  adjust_stock: "info",
  create_transfer: "warning",
  create_hold: "neutral",
  use_hold: "success",
  cancel_hold: "danger",
  reject_transfer_request: "danger",
  cancel_transfer_request: "danger",
  edit_approved_transfer_request: "info",
  receive_transfer_request: "success",
  import_items: "info",
  change_password: "neutral",
};

export default function Audit() {
  const { warehouses } = useAuth();
  const [logs, setLogs] = useState([]);
  const [tabCounts, setTabCounts] = useState({ __all__: 0 });
  const [activeWh, setActiveWh] = useState("__all__");
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filters = useSearchFilterState({
    initialState: {
      topLevelDraft: { ...EMPTY_FILTERS },
      topLevelApplied: { ...EMPTY_FILTERS },
    },
    dateRangeMode: "draft",
    onPageReset: () => setPage(1),
  });
  const draftFilters = filters.state.topLevelDraft;
  const appliedFilters = filters.state.topLevelApplied;
  const dateRangeDraft = filters.state.dateRangeDraft;
  const dateRangeApplied = filters.state.dateRangeApplied;
  const filtersActive = hasTopLevelFilterConstraints(filters.state);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
  });
  const warehouseKey = warehouses.map((warehouse) => warehouse.id).join("|");

  useEffect(() => {
    let current = true;
    const params = { page, page_size: pageSize };
    if (activeWh !== "__all__") params.warehouse_id = activeWh;
    FILTER_KEYS.forEach((key) => {
      const value = String(appliedFilters[key] || "").trim();
      if (value) params[key] = value;
    });
    if (dateRangeApplied.from) params.date_from = dateRangeApplied.from;
    if (dateRangeApplied.to) params.date_to = dateRangeApplied.to;

    setLoading(true);
    setError("");
    api.get("/audit", { params })
      .then(({ data: payload }) => {
        if (!current) return;
        const backendCounts = payload.branch_counts || {};
        const counts = { ...backendCounts };
        counts.__all__ = Object.entries(backendCounts).reduce(
          (total, [branchId, count]) => (
            branchId === "__all__" ? total : total + count
          ),
          0
        );
        const nextPagination = payload.pagination || {
          page,
          page_size: pageSize,
          total: 0,
          total_pages: 0,
        };
        const lastPage = Math.max(nextPagination.total_pages || 0, 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setLogs(payload.data || []);
        setPagination(nextPagination);
        setTabCounts(counts);
      })
      .catch((requestError) => {
        if (!current) {
          return;
        }
        setLogs([]);
        setPagination({ page, page_size: pageSize, total: 0, total_pages: 0 });
        setError(formatApiError(requestError));
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [
    activeWh,
    appliedFilters,
    dateRangeApplied,
    page,
    pageSize,
    retryKey,
    warehouseKey,
  ]);

  const updateDraft = (key, value) => {
    filters.setTopLevelDraft((current) => ({ ...current, [key]: value }));
  };

  const totalPages = Math.max(0, pagination.total_pages);

  const changeWarehouse = (warehouseId) => {
    setActiveWh(warehouseId);
    setPage(1);
  };

  return (
    <OrdinaryPageBody data-testid="audit-page">
      <OrdinaryPageHeader title="Spare Part Audit" actions={<RefreshButton onClick={() => setRetryKey((current) => current + 1)} loading={loading} accessibleLabel="Refresh spare part audit" data-testid="audit-refresh" />} data-testid="audit-page-header" />
      <ListTableShell data-testid="audit-data-panel">
      <WarehouseTabs
        warehouses={warehouses}
        active={activeWh}
        onChange={changeWarehouse}
        showAll
        counts={tabCounts}
        testidPrefix="audit-tab"
      />

      <TopLevelFilterArea className="rounded-none border-x-0 border-t-0 bg-zinc-50/50 px-5 py-3" data-testid="audit-filters">
        <TopLevelFilterRow className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(10rem,1.15fr)_minmax(11rem,1.2fr)_minmax(11rem,1.3fr)_minmax(10rem,1fr)_auto]">
          <TopLevelFilterField label="Action" htmlFor="audit-action-filter">
            <TopLevelSelectFilter id="audit-action-filter" value={draftFilters.action || ""} onChange={(event) => updateDraft("action", event.target.value)} data-testid="audit-action-filter">
              <option value="">All actions</option>
              {ACTION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </TopLevelSelectFilter>
          </TopLevelFilterField>
          <TopLevelFilterField label="Entity Type" htmlFor="audit-entity-filter">
            <TopLevelSelectFilter id="audit-entity-filter" value={draftFilters.entity_type || ""} onChange={(event) => updateDraft("entity_type", event.target.value)} data-testid="audit-entity-filter">
              <option value="">All entity types</option>
              {ENTITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </TopLevelSelectFilter>
          </TopLevelFilterField>
          <TopLevelFilterField label="Ticket Number" htmlFor="audit-ticket-filter">
            <TopLevelTextFilter id="audit-ticket-filter" value={draftFilters.ticket_number || ""} onChange={(event) => updateDraft("ticket_number", event.target.value)} placeholder="Ticket number" data-testid="audit-ticket-filter" />
          </TopLevelFilterField>
          <TopLevelFilterField label="Actor" htmlFor="audit-actor-filter">
            <TopLevelTextFilter id="audit-actor-filter" value={draftFilters.actor || ""} onChange={(event) => updateDraft("actor", event.target.value)} placeholder="Actor email" data-testid="audit-actor-filter" />
          </TopLevelFilterField>
          <TopLevelFilterField label="Summary" htmlFor="audit-summary-filter">
            <TopLevelTextFilter id="audit-summary-filter" value={draftFilters.summary || ""} onChange={(event) => updateDraft("summary", event.target.value)} placeholder="Summary" data-testid="audit-summary-filter" />
          </TopLevelFilterField>
          <DateRangeSelector mode="draft" value={dateRangeDraft} onChange={filters.setDateRangeDraft} id="audit-filter-date" data-testid="audit-filter-date" />
          <div className="flex items-center justify-end gap-1 self-end">
            <TopLevelFilterClear onClick={filters.clearTopLevelFilters} disabled={!filtersActive} data-action-category="utility" data-testid="audit-filter-clear">Clear All</TopLevelFilterClear>
            <TopLevelFilterSearch onClick={filters.applyTopLevelFilters} data-testid="audit-filter-search" />
          </div>
        </TopLevelFilterRow>
      </TopLevelFilterArea>

      <ListTableViewport>
      <ListTable data-testid="audit-table">
        <thead>
          <tr>
            <th style={{width:"190px"}}>Timestamp</th>
            <th style={{width:"180px"}}>Actor</th>
            <th style={{width:"180px"}}>Action</th>
            <th style={{width:"170px"}}>Ticket Number</th>
            <th style={{width:"140px"}}>Entity Type</th>
            <th>Summary</th>
            <th style={{width:"110px"}}>View</th>
          </tr>
        </thead>
        <tbody>
          <ListTableRefreshingRow active={loading && logs.length > 0} colSpan={7} />
          {loading && logs.length === 0 ? (
            <ListTableState colSpan={7} kind="loading">Loading audit events...</ListTableState>
          ) : error ? (
            <ListTableState colSpan={7} kind="error" onRetry={() => setRetryKey((current) => current + 1)}>{error}</ListTableState>
          ) : logs.length === 0 ? (
            <ListTableState colSpan={7}>No audit events.</ListTableState>
          ) : logs.map((record) => {
            const actionLabel = sparePartAuditActionLabel(record.action);
            const ticketNumber = sparePartAuditTicketNumber(record);
            const summary = record.summary || buildSparePartAuditSummary(record, warehouses);
            const badgeTone = BADGE_TONES[record.action] || "neutral";
            return (
              <tr key={record.id} data-testid={`audit-row-${record.id}`}>
                <td className="whitespace-nowrap text-xs font-medium tabular-nums text-zinc-700">{formatPresentationTimestamp(record.created_at)}</td>
                <td className="max-w-[220px] text-xs"><DataValue value={record.actor_email} containment="truncate" /></td>
                <td>
                  <PassiveBadge tone={badgeTone}>
                    {actionLabel}
                  </PassiveBadge>
                </td>
                <td className="max-w-[200px] text-xs font-semibold"><DataValue value={ticketNumber} containment="truncate" /></td>
                <td className="text-xs">{sparePartAuditEntityLabel(record.entity_type)}</td>
                <td className="max-w-lg text-xs text-zinc-600" data-testid={`audit-summary-${record.id}`}>{summary}</td>
                <td>
                  <Button
                    type="button"
                    variant="info"
                    size="row-icon"
                    title="View details"
                    onClick={() => setSelectedRecord(record)}
                    data-action-category="view-info"
                    data-testid={`audit-view-${record.id}`}
                    aria-label={`View details for ${actionLabel}`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </ListTable>
      </ListTableViewport>

      <ListPagination
        page={page}
        pageSize={pageSize}
        total={pagination.total}
        totalPages={totalPages}
        loading={loading}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
        onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setPage(1); }}
        pageSizeAriaLabel="Audit rows per page"
        summaryTestId="audit-pagination-summary"
        pageTestId="audit-pagination-state"
        previousTestId="audit-pagination-prev"
        nextTestId="audit-pagination-next"
      />

      <SparePartAuditDetailDialog
        record={selectedRecord}
        warehouses={warehouses}
        open={Boolean(selectedRecord)}
        onOpenChange={(open) => {
          if (!open) setSelectedRecord(null);
        }}
        badgeTone={selectedRecord ? BADGE_TONES[selectedRecord.action] || "neutral" : "neutral"}
      />
      </ListTableShell>
    </OrdinaryPageBody>
  );
}
